import { useEffect } from 'react';

import type { Maybe } from 'graphql/jsutils/Maybe';
import { useRelayEnvironment } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics/use-analytics-events';
import type {
	ReportingLines,
	ReportingLinesUser,
} from '@atlaskit/teams-client/reporting-lines-client/utils/types';

import { useAppConfig } from '../../../app-config-context';
import { useReportingLinesStore } from '../../main';

export type UseReportingLinesOptions = {
	/**
	 * Cloud id used to build the site ARI required by the KnowledgeDiscovery
	 * `reportingLines` query. Only used when the
	 * `ptc-reporting-lines-knowledge-discovery-query` gate is enabled. Prefer
	 * passing this explicitly for hosts rendered outside of `TeamsWrapper`
	 * (e.g. search). Falls back to the Teams app-config store when not provided.
	 */
	cloudId?: string;
	/**
	 * Org id required by the KnowledgeDiscovery `reportingLines` query. Only used
	 * when the gate is enabled. Falls back to the Teams app-config store when not
	 * provided.
	 */
	orgId?: string;
};

export const useReportingLines = (
	userId: string,
	options?: UseReportingLinesOptions,
): {
	isLoading: boolean;
	reportingLines: ReportingLines | undefined;
	error: Error | undefined;
} => {
	const [
		{ data, isLoading, error },
		{ getReportingLines, getReportingLinesFromKnowledgeDiscovery },
	] = useReportingLinesStore();
	const { fireEvent } = useAnalyticsEvents();
	const environment = useRelayEnvironment();
	const { cloudId: cloudIdFromConfig, orgId: orgIdFromConfig } = useAppConfig();

	const cloudId = options?.cloudId ?? cloudIdFromConfig ?? undefined;
	const orgId = options?.orgId ?? orgIdFromConfig ?? undefined;

	useEffect(() => {
		if (!!cloudId && !!orgId && fg('ptc-reporting-lines-knowledge-discovery-query')) {
			getReportingLinesFromKnowledgeDiscovery(userId, environment, { cloudId, orgId });
		} else {
			getReportingLines(userId);
		}
	}, [
		userId,
		cloudId,
		orgId,
		environment,
		getReportingLines,
		getReportingLinesFromKnowledgeDiscovery,
	]);

	const filterNonactiveUsers = (
		users?: Maybe<ReportingLinesUser[]>,
	): Maybe<ReportingLinesUser[]> => {
		return users?.filter((user) => {
			const isActiveAccountStatus = user.pii?.accountStatus === 'active';
			if (!isActiveAccountStatus) {
				// @ts-ignore - hyphenated action subjects are not supported in @atlaskit/teams-app-internal-analytics
				fireEvent('operational.reporting-lines-user.failed', {
					aaid: user.accountIdentifier || 'undefined',
				});
			}
			return isActiveAccountStatus;
		});
	};

	const filteredReportingLines = data?.reportingLines
		? {
				managers: filterNonactiveUsers(data.reportingLines.managers),
				peers: filterNonactiveUsers(data.reportingLines.peers),
				reports: filterNonactiveUsers(data.reportingLines.reports),
				user: data.reportingLines.user,
				lastModifiedDate: data.reportingLines.lastModifiedDate,
			}
		: data?.reportingLines;

	return {
		isLoading,
		reportingLines: filteredReportingLines,
		error,
	};
};
